import { AppSidebar } from "@/components/AppSidebar";
import { SiteHeader } from "@/components/SiteHeder";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { SidebarInset, SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { DashboardStatCard } from "@/components/stats/DashboardStatCard";
import { UserGrowthChart } from "@/components/stats/UserGrowthChart";
import { LoginHoursChart } from "@/components/stats/LoginHoursChart";
import { InvitationRanking } from "@/components/stats/InvitationRanking";
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { useAdminStatsStore } from "@/stores/adminStats";
import { useEffect } from "react";
import { Ticket, UserCheck, Percent, RefreshCw } from "lucide-react";

const vipSalesConfig: ChartConfig = {
  vipCount: { label: "新增 VIP", color: "#f59e0b" },
};

// PLACEHOLDER_BODY

export function AdminAnalyticsPage() {
  const {
    loading,
    vipSales,
    userGrowth,
    loginDistribution,
    invitation,
    days,
    setDays,
    fetchAll,
  } = useAdminStatsStore();

  useEffect(() => {
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const totalNewVips = vipSales.reduce((s, d) => s + d.vipCount, 0);

  return (
    <SidebarProvider>
      <AppSidebar variant="inset" />
      <SidebarInset>
        <SiteHeader title="数据分析" />
        <div className="flex-1 p-6 space-y-6">
          <div className="flex items-center justify-end gap-3">
            <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="7">近 7 天</SelectItem>
                <SelectItem value="30">近 30 天</SelectItem>
                <SelectItem value="90">近 90 天</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              刷新
            </Button>
          </div>

          <Tabs defaultValue="userGrowth" className="space-y-4">
            <TabsList>
              <TabsTrigger value="userGrowth">用户增长</TabsTrigger>
              <TabsTrigger value="vipSales">VIP 销售</TabsTrigger>
              <TabsTrigger value="loginStats">登录分析</TabsTrigger>
              <TabsTrigger value="invitation">邀请统计</TabsTrigger>
            </TabsList>

            <TabsContent value="userGrowth" className="space-y-4">
              <UserGrowthChart data={userGrowth} />
            </TabsContent>

            <TabsContent value="vipSales" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <DashboardStatCard
                  title="新增 VIP 总数"
                  value={totalNewVips}
                  icon={UserCheck}
                  description={`近 ${days} 天`}
                />
              </div>
              <Card>
                <CardHeader>
                  <CardTitle>VIP 增长趋势</CardTitle>
                  <CardDescription>每日新增 VIP 用户数</CardDescription>
                </CardHeader>
                <CardContent>
                  {vipSales.length > 0 ? (
                    <ChartContainer config={vipSalesConfig} className="h-[300px] w-full">
                      <BarChart data={vipSales}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                        <YAxis tick={{ fontSize: 12 }} allowDecimals={false} />
                        <ChartTooltip content={<ChartTooltipContent />} />
                        <ChartLegend content={<ChartLegendContent />} />
                        <Bar
                          dataKey="vipCount"
                          fill="var(--color-vipCount)"
                          radius={[4, 4, 0, 0]}
                          name="新增 VIP"
                        />
                      </BarChart>
                    </ChartContainer>
                  ) : (
                    <div className="h-[300px] flex items-center justify-center text-muted-foreground">
                      暂无数据
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="loginStats" className="space-y-4">
              <LoginHoursChart data={loginDistribution} />
            </TabsContent>

            <TabsContent value="invitation" className="space-y-4">
              <div className="grid gap-4 md:grid-cols-3">
                <DashboardStatCard
                  title="邀请码总数"
                  value={(invitation?.totalCodes ?? 0).toLocaleString()}
                  icon={Ticket}
                  description="每位用户一个邀请码"
                />
                <DashboardStatCard
                  title="受邀注册用户"
                  value={(invitation?.invitedUsers ?? 0).toLocaleString()}
                  icon={UserCheck}
                  description={`${invitation?.usedCodes ?? 0} 人成功邀请`}
                />
                <DashboardStatCard
                  title="邀请码使用率"
                  value={`${(invitation?.usageRate ?? 0).toFixed(1)}%`}
                  icon={Percent}
                  description="产生邀请的用户占比"
                />
              </div>
              <InvitationRanking data={invitation?.topInvitors ?? []} />
            </TabsContent>
          </Tabs>
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
}

